import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { PageShell, PageHero } from "../components/PageShell";
import { CtaBanner } from "../components/CtaBanner";
import { services, solutions } from "../data/content";

const getContentEntry = (slug, pathname) => {
  const source = pathname.startsWith("/solutions/") ? solutions : services;
  const item = source.find((entry) => entry.slug === slug);

  if (!item) {
    return null;
  }

  return {
    kind: pathname.startsWith("/solutions/") ? "Solution" : "Service",
    item,
    returnLink: pathname.startsWith("/solutions/") ? "/solutions" : "/services",
  };
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const entry = getContentEntry(slug, location.pathname);

  if (!entry) {
    return (
      <PageShell>
        <section className="mx-auto max-w-4xl px-4 py-28 sm:px-6 lg:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">Not found</p>
          <h1 className="mt-5 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            This page isn’t available.
          </h1>
          <p className="mt-5 max-w-xl text-base text-slate-400">
            The content you selected could not be found. Please go back to the service list and choose another option.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/services"
              className="spark-gradient inline-flex items-center gap-2 rounded-full px-6 py-3 font-display text-sm font-semibold text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to services
            </Link>
          </div>
        </section>
      </PageShell>
    );
  }

  const { kind, item, returnLink } = entry;
  const bullets = item.points || item.highlights || [];
  const description = item.long || item.desc || item.short;

  return (
    <PageShell>
      <div data-testid="service-detail-page">
        <PageHero
          overline={kind}
          title={item.title}
          description={description}
        />

        <section className="py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-[1.3fr_0.7fr]">
              <div className="card-gradient rounded-2xl border border-white/[0.08] p-8 sm:p-10">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-400">Overview</p>
                <h2 className="mt-5 font-display text-2xl font-semibold text-white sm:text-3xl">
                  {kind === "Service" ? "Built to solve real business problems." : "Built for meaningful business outcomes."}
                </h2>
                <p className="mt-5 text-base leading-relaxed text-slate-300">
                  {item.short || item.desc}
                </p>
                <p className="mt-4 text-base leading-relaxed text-slate-400">
                  We work with teams to understand the actual workflow, bottleneck, operating model, and commercial goal behind the solution. From there, we shape a practical roadmap, technical architecture, and implementation plan that keeps execution realistic, scalable, and commercially useful.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  {bullets.map((point) => (
                    <span key={point} className="chip">
                      {point}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-white/[0.08] bg-void p-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Why it matters</p>
                <ul className="mt-6 space-y-4">
                  {[
                    "Business-focused delivery",
                    "Scalable, maintainable architecture",
                    "Clear communication and measurable outcomes",
                  ].map((value) => (
                    <li key={value} className="flex items-start gap-3 text-sm text-slate-300">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
                      <span>{value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-12 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-8 sm:p-10">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-400">How we approach it</p>
              <div className="mt-6 grid gap-6 md:grid-cols-3">
                <div>
                  <p className="font-display text-xl font-semibold text-white">Understand</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">
                    We start by clarifying the business challenge, the user journey, and the operational constraints that matter most.
                  </p>
                </div>
                <div>
                  <p className="font-display text-xl font-semibold text-white">Design</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">
                    We shape the right technical approach, define the system boundaries, and align the build around reliability and maintainability.
                  </p>
                </div>
                <div>
                  <p className="font-display text-xl font-semibold text-white">Deliver</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">
                    We build, refine, test, and support the final delivery so the solution is ready for business use and future growth.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="spark-gradient inline-flex items-center gap-2 rounded-full px-6 py-3 font-display text-sm font-semibold text-white"
              >
                Start a conversation
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        <CtaBanner
          title={kind === "Service" ? `Need help with ${item.title}?` : `Looking for a ${item.title.toLowerCase()} solution?`}
          text="Tell us what you’re building, what problem you’re solving, and where your team needs support. We’ll help shape the right approach."
        />
      </div>
    </PageShell>
  );
}
